import { computed, defineAsyncComponent, Ref, shallowRef } from 'vue';

import type { MarketModel } from '@exchange/libs/market/service/src';
import { modalService } from '@exchange/libs/modals/src';
import { OrderSide, OrderType } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import { settingsService } from '@exchange/libs/settings/service/src';
import { gaService, GACategory, GAImmediateFillClickedAction, GAMoveMarketClickedAction } from '@exchange/libs/utils/google-analytics/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';

import { doesOrderMovePrice, isLimitOrderRisky, isStopOrderRisky, WarningModalsOrderBook } from './check-order-price';

const MarketMoveModal = defineAsyncComponent(() => import(/* webpackChunkName: "chunk-warning-modals" */ './components/warning-modals/MarketMoveModal.vue'));
const PriceWarningModal = defineAsyncComponent(() => import(/* webpackChunkName: "chunk-warning-modals" */ './components/warning-modals/PriceWarningModal.vue'));

interface WarningModalIds {
  price: string | undefined;
  move: string | undefined;
}

const useWarningModals = (market: Ref<MarketModel>, emptyValuesDefault: string) => {
  const warningModalIds: WarningModalIds = {
    price: undefined,
    move: undefined,
  };
  const marketName = computed(() => {
    if (!market.value) {
      return emptyValuesDefault;
    }

    return `${market.value.base.code}/${market.value.quote.code}`;
  });

  const lastMatchedPrice = computed(() => {
    if (!market.value) {
      return NaN;
    }

    return market.value.lastPrice;
  });

  const orderConfirmations = computed(() => settingsService.settings.userSettings.orderConfirmations);

  const closeModal = (action: () => void) => () => {
    modalService.clear();
    action();
  };

  const trackPriceWarningClickEvents = (type: OrderType, eventAction: GAImmediateFillClickedAction, dontShowWarning: boolean, tv: number) => {
    const category = GACategory.IMMEDIATE_FILL_WARNING_CLICKED.replace('{order_type}', type.toLowerCase()) as GACategory;

    gaService.trackGAEvent(category, eventAction, dontShowWarning.toString(), tv);
  };

  const trackMarketMoveClickEvents = (eventAction: GAMoveMarketClickedAction, dontShow: boolean, movePercent: number) => {
    gaService.trackGAEvent(GACategory.MOVE_MARKET_WARNING_CLICKED, eventAction, dontShow.toString(), movePercent);
  };

  const isOrderConfirmationEnabled = (type: OrderType) => orderConfirmations.value[type.toLowerCase()];

  const needToShowPriceWarningModal = (
    storedType: OrderType.LIMIT | OrderType.STOP,
    storedSide: OrderSide,
    orderbook: WarningModalsOrderBook,
    limitPrice: number,
    stopPrice: number,
  ) => {
    if (!isOrderConfirmationEnabled(storedType)) {
      return false;
    }

    switch (storedType) {
      case OrderType.LIMIT:
        return isLimitOrderRisky({
          side: storedSide,
          limit: limitPrice || 0,
          bestAsk: orderbook.asks[0]?.price || 0,
          bestBid: orderbook.bids[0]?.price || 0,
        });
      case OrderType.STOP:
        return isStopOrderRisky({
          side: storedSide,
          limit: limitPrice || 0,
          stop: stopPrice || 0,
        });
      default:
        logger.log(`Unknown type: ${storedType}`);
        return false;
    }
  };

  const needToShowMoveWarningModal = (storedSide: OrderSide, orderbook: WarningModalsOrderBook, amount: number) => {
    const { percent, should } = doesOrderMovePrice({
      side: storedSide,
      amount: amount || 0,
      lastPrice: lastMatchedPrice.value || 0,
      orderbook,
    });

    return {
      should,
      percent,
    };
  };

  const showMoveWarningModal = async (percent: number, storedType: OrderType, storedSide: OrderSide) =>
    new Promise<void>((resolve, reject) => {
      warningModalIds.move = modalService.show(
        shallowRef(MarketMoveModal),
        {
          type: storedType,
          side: storedSide,
          'market-name': marketName.value,
          'move-percent': percent,
          trackClickEvents: trackMarketMoveClickEvents,
          saveSetting: (dontShowWarningAgain: boolean) =>
            settingsService
              .setSettings({
                path: 'orderConfirmations.market',
                value: !dontShowWarningAgain,
              })
              .catch((error) => {
                logger.log('Saving "orderConfirmations.market" setting failed', error);
              }),
        },
        {
          'submit-market-move': closeModal(resolve),
          'decline-market-move': closeModal(reject),
          'modals-container-on-outside-click': () => {
            /** modal is being hidden by ModalsContainer.vue */
            trackMarketMoveClickEvents(GAMoveMarketClickedAction.OUTSIDE, Boolean(settingsService.settings.userSettings.orderConfirmations.market), percent);
            closeModal(reject);
          },
        },
        { isPrivate: true },
      );
    });

  const showPriceWarningModal = async (storedType: OrderType, storedSide: OrderSide, orderbook: WarningModalsOrderBook, limitPrice: number) =>
    new Promise<void>((resolve, reject) => {
      warningModalIds.price = modalService.show(
        shallowRef(PriceWarningModal),
        {
          type: storedType,
          side: storedSide,
          'limit-price': limitPrice,
          'best-bid': orderbook.bids[0]?.price || 0,
          'best-ask': orderbook.asks[0]?.price || 0,
          'market-name': marketName.value,
          trackClickEvents: trackPriceWarningClickEvents,
          saveSetting: (dontShowWarningAgain: boolean) => {
            const settingPath = `orderConfirmations.${storedType.toLowerCase()}`;

            return settingsService
              .setSettings({
                path: settingPath,
                value: !dontShowWarningAgain,
              })
              .catch((error) => {
                logger.log(`Saving "${settingPath}" setting failed`, error);
              });
          },
        },
        {
          accept: closeModal(resolve),
          cancel: closeModal(reject),
          'modals-container-on-outside-click': () => {
            /** modal is being hidden by ModalsContainer.vue */
            const orderType = storedType.toLowerCase();

            trackPriceWarningClickEvents(
              storedType,
              GAImmediateFillClickedAction.OUTSIDE,
              Boolean(settingsService.settings.userSettings.orderConfirmations[orderType]),
              limitPrice,
            );
            closeModal(reject);
          },
        },
        { isPrivate: true },
      );
    });

  const handleRiskyOrders = async (
    storedType: OrderType,
    storedSide: OrderSide,
    orderbook: WarningModalsOrderBook,
    postOnly: boolean,
    amount: number,
    limitPrice: number,
    stopPrice: number,
  ) => {
    switch (storedType) {
      case OrderType.MARKET: {
        const { percent, should } = needToShowMoveWarningModal(storedSide, orderbook, amount);

        if (should && percent !== undefined) {
          await showMoveWarningModal(percent, storedType, storedSide);
        }

        break;
      }
      case OrderType.LIMIT:
      case OrderType.STOP: {
        if ((storedType === OrderType.LIMIT ? !postOnly : true) && needToShowPriceWarningModal(storedType, storedSide, orderbook, limitPrice, stopPrice)) {
          await showPriceWarningModal(storedType, storedSide, orderbook, limitPrice);
        }

        break;
      }
      default:
        logger.log(`Unknown type: ${storedType}`);
    }
  };

  return {
    handleRiskyOrders,
  };
};

export default useWarningModals;
